"use client"

import TokenChecker from "../TokenChecker"
import { ArrowLeft } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

export default function FlowsynSetup() {

    const navigate = useNavigate()
    const URL_PreFix = "/api/flowsync/"
    const [connectError, setConnectError] = useState(null)
    const [isConnecting, setIsConnecting] = useState(false)

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await TokenChecker(`${URL_PreFix}connection/status/`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json'
          }
        })
        if (!response.ok) return
        const data = await response.json()
        if (data.connected) {
          navigate('/flowsync')
        }
      } catch (error) {
        console.error('Error checking Gmail connection:', error?.response?.status, error?.code)
      }
    }

    checkConnection()
  }, [URL_PreFix, navigate])

  const handleConnectGmail = async () => {
    if (isConnecting) return
    setIsConnecting(true)
    setConnectError(null)
    try {
      const response = await TokenChecker(`${URL_PreFix}oauth/google/initiate/`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to initiate Gmail OAuth')
      }

      const data = await response.json()
      
      // Redirect to Google OAuth
      if (data.auth_url) {
        window.location.href = data.auth_url
      } else {
        throw new Error('No authorization URL received')
      }

    } catch (error) {
      console.error('Error connecting Gmail:', error?.response?.status, error?.code)
      setConnectError(error?.response?.data?.error || 'Failed to connect Gmail. Please try again.')
      setIsConnecting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
     <header className="bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
  <div className="flex items-center gap-4">
    {/* Back to Home button */}
    <button
      onClick={() => navigate('/home')}
      className="flex items-center justify-center w-10 h-10 rounded-full border border-[#CFE8FB] bg-[#EBF8FF] text-[#4299E1] hover:bg-[#D7EEFF] hover:text-[#3182CE] transition-all duration-200 shadow-sm"
      title="Back to Home"
    >
      <ArrowLeft className="w-5 h-5" />
    </button>

    {/* FlowSync Branding */}
    <div>
      <span className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
        FlowSync
      </span>
      <p className="text-sm text-gray-500">
        AI-powered task extraction and email response generation
      </p>
    </div>
  </div>
</header>


      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          {/* Gmail Icon */}
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-200">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
                <path fill="#4caf50" d="M45,16.2l-5,2.75l-5,4.75L35,40h7c1.657,0,3-1.343,3-3V16.2z"></path>
                <path fill="#1e88e5" d="M3,16.2l3.614,1.71L13,23.7V40H6c-1.657,0-3-1.343-3-3V16.2z"></path>
                <polygon fill="#e53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.95 35,23.7 36,17"></polygon>
                <path fill="#c62828" d="M3,12.298V16.2l10,7.5V11.2L9.876,8.859C9.132,8.301,8.228,8,7.298,8h0C4.924,8,3,9.924,3,12.298z"></path>
                <path fill="#fbc02d" d="M45,12.298V16.2l-10,7.5V11.2l3.124-2.341C38.868,8.301,39.772,8,40.702,8h0 C43.076,8,45,9.924,45,12.298z"></path>
              </svg>
            </div>
          </div>

          {/* Title & Description */}
          <div className="text-center mb-10">
            <h2 className="text-xl font-medium text-gray-900 mb-3">
              Connect Your Gmail Account
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed max-w-md mx-auto">
              FlowSync needs access to your Gmail to automatically extract tasks from your emails and generate intelligent responses. Your data is secure and never shared.
            </p>
          </div>

          {/* Connect Button */}
          <div className="mb-10">
            <button
              onClick={handleConnectGmail}
              disabled={isConnecting}
              className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
            >
              {isConnecting ? 'Connecting…' : 'Connect to Gmail'}
            </button>
            {connectError && (
              <p className="mt-3 text-sm text-red-600 text-center">{connectError}</p>
            )}
          </div>

          {/* Features */}
          <div className="pt-8 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-900 mb-4">
              What you'll get:
            </h3>
            <div className="space-y-3">
              {[
                "Automatic task extraction from emails",
                "AI-generated email responses",
                "Priority detection and categorization",
                "Seamless integration with FlowMan",
              ].map((feature, index) => (
                <div key={index} className="flex items-start">
                  <svg
                    className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
